
<template>
  <div>
    <div class="colour-legend">
      <h4 id="colour-legend">
        Legend:
      </h4>
      <ul class="clean-list">
        <li><div class="fc-daygrid-event-dot" style="border-color: orange;" />One-off Event</li>
        <li><div class="fc-daygrid-event-dot" style="border-color: purple;" />Recurring Event</li>
        <li><div class="fc-daygrid-event-dot" style="border-color: red;" />Errored/Broken Event</li>
      </ul>
    </div>
    <b-form-checkbox v-model="show_recurring"
                     plain
                     class="my-3"
                     value="showing"
                     unchecked-value="hidden"
                     @change="() => handleCustomButtonRefreshCalendar({ forget_cache: false })">
      <b>Show recurring events?</b>
    </b-form-checkbox>
    <loading :active.sync="is_loading"
             :can-cancel="true"
             loader="dots"
             color="#56B2BF"
             :blur="null"
             :is-full-page="is_full_page" />

    <full-calendar ref="fullCalendar" :options="calendar_options" />

    <delete-confirm-modal :entities-to-delete="[selected_event]"
                          entity-type="Event" />

    <calendar-day-modal v-bind="day_modal_props" @next-modal="handleEventClick" />
    <calendar-event-modal v-bind="event_modal_props" />
    <quick-alert-modal v-bind="quick_alert" scope="calendar-view" />
  </div>
</template>

<script>
import _throttle from 'lodash/throttle';

import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import toastr from 'toastr';
// Loader cmp/stylesheet
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

import DeleteConfirmModal from 'VueModals/DeleteConfirmModal';
import CalendarDayModal from 'VueModals/CalendarDayModal';
import CalendarEventModal from 'VueModals/CalendarEventModal';

export default {
  components: {
    FullCalendar,
    Loading,
    DeleteConfirmModal,
    CalendarDayModal,
    CalendarEventModal,
  },
  data() {
    return {
      day_modal_props: {
        events: [],
      },
      event_modal_props: {
        entry: {}, // for editing
        refreshCalendar: () => 1,
        fullCalendar: null,
        startStr: ``,
        endStr: ``,
        recurring: false,
        recurFrequency: ``,
        duration: 0,
        streamName: ``,
        description: ``,
        imgSrc: ``,
        imgAlt: ``,
        userTimezone: 'Unknown',
      },
      show_recurring: `showing`,
      is_loading: false,
      is_full_page: true,
      events: {},
      calendar_options: {
        // nextDayThreshold: "00:00:00",
        customButtons: {
          refreshCalendar: {
            text: 'Refresh Calendar',
            click: this.handleCustomButtonRefreshCalendar,
          },
        },
        eventDataTransform: (ed) => {
          this.events[ed.id] = ed;

          return ed;
        },
        firstDay: 1,
        weekNumbers: true,
        weekText: 'Week ',
        slotDuration: '00:15:00',
        slotEventOverlap: false,
        plugins: [
          dayGridPlugin,
          timeGridPlugin,
          interactionPlugin,
          listPlugin,
          momentTimezonePlugin,
        ],
        loading: this.handleLoading,
        selectable: true,
        navLinks: true,
        timeZone: window.user.timezone,
        eventTimeFormat: { // like '14:30:00'
          hour: '2-digit',
          minute: '2-digit',
          meridiem: false,
          hour12: false,
        },
        nowIndicator: true,
        initialView: 'dayGridMonth',
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
        },
        footerToolbar: {
          center: 'refreshCalendar',
        },
        buttonText: {
          today: `Today`,
          dayGridMonth: `Month`,
          timeGridWeek: `Week`,
          timeGridDay: `Day`,
          listWeek: `List`,
        },
        eventClick: this.handleEventClick,
        select: this.handleSelect,
        selectAllow: this.handleSelectAllow,
        events: this.handleEvents,
        eventContent: this.handleEventContent,
        eventDidMount: this.handleEventDidMount,
        viewDidMount: this.handleViewDidMount,
        dayCellClassNames: this.handleDayCellClassNames,
        dayCellDidMount: this.handleDayCellDidMount,
        moreLinkClick: this.handleMoreLinkClick,
        weekNumberDidMount: this.handleWeekNumberDidMount,
        contentHeight: 800,
        expandRows: true,
        dayMaxEventRows: true, // for all non-TimeGrid views
        windowResize: this.windowResizedAdjustments,
      },
      forget_cache: false,
      resize_handler: null,
      resize_handler_delay: 250,
      selected_event: null,
    };
  },

  computed: {
    recurring_text() {
      if (this.show_recurring) {
        return `Hide recurring`;
      }
      return `Show recurring`;
    },
    api_endpoint() {
      const endpoint = new URL(`${window.location.protocol}//${window.location.host}/api/v1/scheduler?calendar=true`);
      const params = endpoint.searchParams;
      params.set(`forget`, this.forget_cache);
      params.set(`ttype`, this.show_recurring === `hidden` ? `once` : `any`);
      endpoint.search = params.toString();
      return endpoint.toString();
    },
  },

  mounted() {
    this.calendar_options.headerToolbar.right = 'dayGridMonth,timeGridWeek,timeGridDay,listWeek';

    // dom manip to get refresh btn at the top:
    const header_btns_el = document.querySelector(`.fc-header-toolbar .fc-toolbar-chunk`);
    const bottom_refesh_btn_el = document.querySelector(`.fc-footer-toolbar .fc-toolbar-chunk .fc-refreshCalendar-button`);
    const cloned_btn_el = bottom_refesh_btn_el.cloneNode(true);
    cloned_btn_el.addEventListener(`click`, () => bottom_refesh_btn_el.click()); // clone does not add listeners, so we need to 'click' the real button as our handler
    header_btns_el.append(cloned_btn_el);
    bottom_refesh_btn_el.style.display = `none`; // need to hide bottom btn for some reason (feedback)

    // hijack the day/week view buttons to force a view refresh
    // basically, when one of these is clicked, we click back to month view and THEN to the desired view
    // we set up a guard to prevent the second click from causing this code to loop
    const grid_week_btn_el = document.querySelector(`.fc-timeGridWeek-button`);
    const grid_day_btn_el = document.querySelector(`.fc-timeGridDay-button`);
    const grid_month_btn_el = document.querySelector(`.fc-dayGridMonth-button`);
    let click_guard = false;
    [grid_week_btn_el, grid_day_btn_el].forEach((btn_el) => {
      // on day/week click...
      btn_el.addEventListener(`click`, () => {
        // if guard is not set...
        if (!click_guard) {
          // set the guard
          click_guard = true;
          // click the month view btn to clear views
          grid_month_btn_el.click();
          // after a short delay, re-click whichever button the user initially clicked:
          setTimeout(() => {
            btn_el.click();
            click_guard = false; // and release the guard
          }, 100);
        }
      });
    });

    this.windowResizedAdjustments({ immediate: true });

    // watch the fullcalendar's content for DOM updates
    const obs = new MutationObserver(_throttle(() => {
      obs.disconnect(); // while handling, don't listen for further events

      const is_month_view = document.querySelector('.fc-daygrid');

      if (is_month_view) {
        const day_cell_els = [...document.querySelectorAll('.fc-daygrid-day')];
        for (const cell of day_cell_els) {
          const event_els = [
            ...cell.querySelectorAll('.fc-daygrid-event-harness'),
          ];

          // for each event el, we need to remove all its duplicates
          for (const [i, event_el] of event_els.entries()) {
            for (const [j, other_el] of event_els.entries()) {
              if (i === j) continue;
              if (event_el.textContent === other_el.textContent) {
                other_el.remove();
                event_els.splice(j, 1);
              }
            }
          }
        }
      } else {
        // here we are selecting all the event cards
        const displayed_event_els = [
          ...document.querySelectorAll('.fc-event-main-frame'),
        ];

        // for each of those, we update the displayed times
        for (const displayed_event_el of displayed_event_els) {
          const [display_time_el, display_title_el] = [
            displayed_event_el.querySelector('.fc-event-time'), // time display
            displayed_event_el.querySelector('.fc-event-title, .fc-sticky'), // title display
          ];

          // search by title
          const event = Object.values(this.events).find(ev => ev.title === display_title_el.textContent);

          if (event?.extendedProps.originalStartTime) {
            const start_str = event.extendedProps.originalStartTime.split(':').slice(0, -1).join(':');
            const end_str = event.extendedProps.originalEndTime.split(':').slice(0, -1).join(':');
            display_time_el.textContent = `${start_str} - ${end_str}`;
          }
        }
      }

      // begin observing again
      obs.observe(el, {
        childList: true,
        subtree: true,
      });
    }, 100));

    // we observe this element for mutations
    const el = document.querySelector('.fc-view-harness');
    obs.observe(el, {
      childList: true,
      subtree: true,
    });
  },

  methods: {

    /**
     * When window is resized and it is too smol, it will change the view and remove options
     */
    windowResizedAdjustments(options = { immediate: false }) {
      const calendarViewCheck = () => {
        const calendar_api = this.$refs.fullCalendar.getApi();
        if ($(window).width() < 765) {
          if (calendar_api.view.type !== 'timeGridDay') {
            calendar_api.changeView('listWeek');
          }
          this.calendar_options.headerToolbar.right = 'timeGridDay,listWeek';
        } else {
          this.calendar_options.headerToolbar.right = 'dayGridMonth,timeGridWeek,timeGridDay,listWeek';
        }
      };

      window.addEventListener('resize', () => {
        if (!this.nullish(this.resize_handler)) clearTimeout(this.resize_handler);
        this.resize_handler = setTimeout(calendarViewCheck, this.resize_handler_delay);
      });

      if (options.immediate) {
        calendarViewCheck();
      }
    },

    /**
     * Called right after the <td> has been added to the DOM
     *
     * https://fullcalendar.io/docs/day-cell-render-hooks
     */
    handleDayCellDidMount(e) {
    // Disable cells in the past
      if (e.isPast) {
        e.el.style.backgroundColor = '#F1F1F1';
      }
    },

    handleWeekNumberDidMount(e) {
      e.el.style.backgroundColor = "rgba(0,0,0,0)";
    },

    calcEventColour(event_props) {
      if (event_props.status === `ERR`) return `red`;
      else {
        if (event_props.recurring) {
          return `purple`;
        } else {
          return `orange`;
        }
      }
    },

    /**
     * A Content Injection Input. Generated content is inserted inside the inner-most wrapper of the event element.
     * If supplied as a callback function, it is called every time the associated event data changes.
     *
     * https://fullcalendar.io/docs/event-render-hooks#argument
     */
    handleEventContent(arg) {
      const event = arg.event;
      const event_colour = this.calcEventColour(event.extendedProps);
      switch (arg.view.type) {
        case `dayGridMonth`:
          if (event.extendedProps.recurring && this.show_recurring === `hidden`) {
            return {
              html: ``,
            };
          }
          return {
            html: `
              <div class="fc-daygrid-event-dot" style="border-color: ${event_colour};"></div>
              <img style='height: 35px; width: 35px' src='${event.extendedProps.image}' />
              <div style='margin-left: 10px; font-size: 10px; ' class='fc-scheduler-title'>${event.title}</div>
            `,
          };
        case `timeGridWeek`:
          return {
            html: `
              <span>${event.title}</span>
            `,
          };
        case `listWeek`:
          return {
            html: `
              <span class="fc-list-event-dot mr-1" style="border-color: ${event_colour};"></span>
              ${event.title}
            `,
          };
      }
    },

    /**
     * https://fullcalendar.io/docs/event-render-hooks
     */
    handleEventDidMount(arg) {
      // colouring for weekview boxes
      if (arg.view.type === `timeGridWeek` || arg.view.type === `timeGridDay`) {
        const event = arg.event;
        const colour = this.calcEventColour(event.extendedProps);
        const box_el = arg.el;
        box_el.style.overflow = `auto`;
        box_el.style.backgroundColor = colour;
        box_el.style.borderColor = colour;
        if (colour === `orange`) {
          box_el.firstChild.style.color = `black`;
        }
      }
    },

    /**
     * https://fullcalendar.io/docs/view-render-hooks
     */
    handleViewDidMount(arg) {
      // more manip to attach a class to the cal button controls
      // needs to happen on view mount since fc clears the classlist of the buttons every time...
      const cal_btn_els = document.querySelectorAll(`.fc-button`);
      for (const btn_el of cal_btn_els) {
        btn_el.classList.add(`fc-button-override`);
      }

      if (arg.view.type !== 'dayGridMonth') {
        $('.fc-recurringToggle-button').css('display', 'none');
      } else {
        $('.fc-recurringToggle-button').css('display', 'block');
      }
    },

    /**
     * https://fullcalendar.io/docs/loading
     */
    handleLoading(is_loading) {
      this.is_loading = is_loading;
    },

    async handleMoreLinkClick(arg) {
      this.handleSelect(arg);
    },

    /**
     * When selected a date or date and time
     *
     * https://fullcalendar.io/docs/select-callback
     */
    async handleSelect(selection_info) {
      // Get current selection day events (the only way I found it possible is by filtering all events)
      const events = this.$refs.fullCalendar
        .getApi()
        .getEvents()
        .filter(ev => moment(selection_info.start ?? selection_info.date).isSame(moment(ev.start), 'day'));
        // .reduce((events, ev) => {
        //   if (!events.find(other => other.id === ev.id)) events.push(ev); // event must have unique id
        //   return events;
        // }, []);

      this.day_modal_props = {
        events,
        title: selection_info.view.type === `dayGridMonth` ? moment(selection_info.start ?? selection_info.date).format(`lll`) : `${moment(selection_info.start).format(`lll`)} - ${moment(selection_info.end).format(`lll`)}`,
      };
      const response = await this.fireModal(`cal-day-modal`);
      if (response.data.confirm) {
        if (selection_info.view.type === `dayGridMonth`) {
          this.newEvent(selection_info.view, {
            date_start_end: selection_info.startStr,
          });
        } else {
          this.newEvent(selection_info.view, {
            startStr: moment(selection_info.startStr),
            endStr: moment(selection_info.endStr),
          });
        }
      }
    },

    newEvent(view, date_strings) {
      if (view.type === 'dayGridMonth') {
        window.location.href = `/scheduler/create?date=${date_strings.date_start_end}`;
      } else {
        window.location.href = `/scheduler/create?start=${moment(date_strings.startStr).unix()}&end=${moment(date_strings.endStr).unix()}`;
      }
    },

    async deleteEvent(event) {
      this.selected_event = event;
      try {
        const modal_response = await this.fireModal(`delete-confirm`);
        if (modal_response.data.confirm) {
          const api_response = await axios.delete(`/api/v1/scheduler`, {
            params: { 'ids': event.id },
          });

          if (api_response.data.success) {
            toastr.success(`Successfully deleted ${event.extendedProps.recurring ? `recurring` : ``} event!`);
            this.handleCustomButtonRefreshCalendar({
              forget_cache: true,
            });
          } else {
            toastr.error(`Something went wrong deleting event.`);
          }
        }
      } catch (err) {
        console.error(err);
        this.handleAxiosError(err);
      }
    },

    handleSelectAllow(selectInfo) {
      // if it is an allday event allow same day selection to add an event.
      let todayMoment = moment();
      if (selectInfo.allDay) {
        todayMoment = moment().subtract(1, 'day');
      }

      // if attempted selection is before now, do not allow it.
      if (moment(selectInfo.start).isSameOrBefore(todayMoment)) {
        return false;
      }

      return true;
    },

    /**
     * When clicked on specific event
     */
    async handleEventClick(event_info) {
      const cal_event = event_info.event;
      const event = Object.values(this.events).find(ev => ev.id === cal_event.id);

      const start_str = (() => {
        if (event.extendedProps.recurring) {
          const start_time = moment(event.extendedProps.originalStartTime ?? event.startTime, "HH:mm:ss");
          const start_date = moment(cal_event._instance.range.start)
            .set('hours', start_time.hours())
            .set('minutes', start_time.minutes())
            .set('seconds', start_time.seconds());

          return start_date.format("YYYY-MM-DD HH:mm:ss");
        }

        return event.start ?? event.startTime;
      })();

      const end_str = (() => {
        if (event.extendedProps.recurring) {
          const start_time = moment(event.extendedProps?.originalStartTime ?? event.startTime, "HH:mm:ss");
          const end_time = moment(event.extendedProps?.originalEndTime ?? event.endTime, "HH:mm:ss");
          const crosses_midnight = start_time > end_time; // the start is 'after' the end; it's an event which crossed midnight from yesterday
          const date_offset = crosses_midnight ? 1 : 0;

          const end_date = moment(cal_event._instance.range.start)
            .add(date_offset, 'days')
            .set('hours', end_time.hours())
            .set('minutes', end_time.minutes())
            .set('seconds', end_time.seconds());

          return end_date.format("YYYY-MM-DD HH:mm:ss");
        }

        return event.end ?? event.endStr;
      })();

      this.event_modal_props = {
        entry: event.extendedProps.entry, // for editing
        refreshCalendar: this.handleCustomButtonRefreshCalendar,
        fullCalendar: this.$refs.fullCalendar,
        title: `${moment(start_str).format(`ddd Do`)}: ${event.title}`,
        startStr: start_str,
        endStr: end_str,
        imgSrc: event.extendedProps.image,
        imgAlt: event.title,
        recurring: event.extendedProps.recurring,
        recurFrequency: event.extendedProps?.recurring_data?.freq || 'once',
        description: event.extendedProps.description || ``,
        streamName: event.extendedProps.streamname,
        duration: event.extendedProps.duration,
        dot_colour: this.calcEventColour(event.extendedProps),
        userTimezone: window.user.timezone,
        endRecur: event.extendedProps?.entry?.endsat || 'Forever',
      };

      this.$modal.hideAll(); // just in case
      const response = await this.fireModal(`cal-event-modal`);
      const scheduler_response = response.data.data;
      if (response.data.confirm) {
        if (!scheduler_response) {
          this.deleteEvent(event);
        } else {
          if (scheduler_response.status !== 200) {
            this.quickAlert({
              title: "Error",
              message: "Could not update scheduled event. Please contact Sharpstream support.",
            }, "calendar-view");
          } else {
            this.quickAlert({
              title: "Success!",
              message: "Scheduled event successfully updated.",
            }, "calendar-view");
          }
        }
      }
    },

    humanDuration(duration) {
      return moment.duration(parseInt(duration), "seconds").format("h [hrs] m [min]");
    },

    async handleEvents(info, success_callback, failure_callback) {
      const response = await axios.get(this.api_endpoint);
      if (response.status === 200) {
        this.forget_cache = false;
        success_callback(response.data);
      } else {
        this.forget_cache = false;
        toastr.success('Failed to load calendar data!');
        failure_callback('Failed to load calendar data!');
      }
    },

    async handleCustomButtonRefreshCalendar(options = { forget_cache: true }) {
      this.forget_cache = options.forget_cache;
      // Workaround.. Assume true if undefined.
      // The value is not appended but the event is still needed to get this.$ref
      // It still works without refreshing unnecssearay when navigating.
      if (_.isUndefined(this.forget_cache)) {
        this.forget_cache = true;
      }
      this.$refs.fullCalendar.getApi().refetchEvents();
    },

  },
};

</script>

<style lang="scss">
@use "sass:color";
@use "sass:map";

@use "~AssetsSass/_variables" as *;
@use "~AssetsSass/buttons";

@import "~AssetsCss/fullcalendar-vars.css";

.fc-button-override {
  @extend %btn-ss-base;
  color: var(--white) !important;
  background-color: map.get($ss, 'primary') !important;
  border-color: map.get($ss, 'primary') !important;
  &:hover {
    border-color: map.get($ss-adds, 'primary-hover') !important;
    background-color: map.get($ss-adds, 'primary-hover') !important;
  }
  &.fc-button-active {
    background-color: color.adjust(map.get($ss, 'primary'), $lightness: -5%, $space: hsl) !important;
  }
}

.fc-list-event-graphic {
  display: none;
}

.colour-legend {
  display: inline-block;
  border: 1px solid map.get($ss, 'primary');
  background-color: map.get($ss-adds, 'border-gray');
  border-radius: 4px;
  padding: 0.6em 3em 0.1em 0.6em;
}

.fc-daygrid-event-dot {
  display: inline-block;
}

.fc-timegrid-now-indicator-line {
  border-width: 2px 0 0 !important;
}
</style>
